import { useState } from 'react'
import { Link } from 'react-router-dom'
import './TattooTherapy.css'

const API_ENDPOINT = 'https://forms.2merative.com/api/contact.php'

const therapyFeatures = [
  {
    icon: '🌿',
    title: 'A Considered Pace',
    desc: 'Sessions are never rushed. We take the time to talk, plan, and make sure you feel completely comfortable before anything begins.',
  },
  {
    icon: '🤍',
    title: 'A Safe, Private Space',
    desc: 'Your story stays with us. Tattoo Therapy sessions are held with full discretion, care, and respect for your privacy.',
  },
  {
    icon: '✦',
    title: 'Meaningful Design',
    desc: 'Every Therapy tattoo is deeply personal, memorial pieces, healing symbols, cover-ups, or designs that mark a new chapter.',
  },
  {
    icon: '💬',
    title: 'Free Consultation',
    desc: 'We always start with a conversation, in person or online, before any booking is confirmed. No pressure, ever.',
  },
]

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  service: 'TATTOO THERAPY PACKAGE',
  appointmentType: 'COMMEMORATIVE OR RITUAL BASED',
  preferredDate: '',
  tattooDetails: '',
}

export default function TattooTherapy() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState(initialForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState({
    type: '',
    message: '',
  })

  function openModal() {
    setIsModalOpen(true)
    setSubmitStatus({ type: '', message: '' })
  }

  function closeModal() {
    setIsModalOpen(false)
    setSubmitStatus({ type: '', message: '' })
  }

  function handleChange(event) {
    const { name, value } = event.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: '', message: '' })

    try {
      const payload = new FormData()
      payload.append('name', `${formData.firstName} ${formData.lastName}`.trim())
      payload.append('email', formData.email)
      payload.append('phone', formData.phone)
      payload.append('subject', 'Tattoo Therapy Contact Form')
      payload.append(
        'message',
        [
          'Tattoo Therapy enquiry',
          '',
          `First Name: ${formData.firstName}`,
          `Last Name: ${formData.lastName}`,
          `Email: ${formData.email}`,
          `Phone: ${formData.phone || 'Not provided'}`,
          `Service Interested In: ${formData.service}`,
          `Appointment Type: ${formData.appointmentType}`,
          `Preferred Date: ${formData.preferredDate || 'Not provided'}`,
          '',
          'Tattoo Details:',
          formData.tattooDetails,
        ].join('\n')
      )
      payload.append('form_name', 'Tattoo Therapy Contact Form')

      await fetch(API_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        body: payload,
      })

      setSubmitStatus({
        type: 'success',
        message: 'Thanks. Your message has been sent. We will get back to you within 48 hours.',
      })
      setFormData(initialForm)
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'There was a problem sending your message. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <section className="therapy section" aria-labelledby="therapy-heading">
        <div className="container">
          <div className="therapy__inner">
            <div className="therapy__content">
              <span className="therapy__eyebrow">Where tattooing meets therapeutic care</span>

              <h2 className="therapy__heading" id="therapy-heading">
  <span className="therapy__heading-text">
    Tattoo
    <span className="therapy__heading-script"> Therapy</span>
  </span>

  <img
    src="/Ink_logo.png"
    alt="Inked with Intention logo"
    className="therapy__logo"
  />
</h2>

              <p className="therapy__lead">
                Held with presence, intention, and respect for both body and story.
              </p>

              <p className="therapy__body">
                Hi, I&apos;m Jesse. I&apos;m a qualified counsellor with a strong focus on
                building safety, trust, and connection. I work gently and collaboratively,
                supporting people to explore meaning and emotion while staying grounded in
                their body and experience.
              </p>

              <p className="therapy__body">
                My work draws on Emotion Focused, Person-Centred, and Attachment theories.
                In June 2025, I gained my Diploma in Counselling and Psychotherapy and
                continued my studies toward completing my bachelor&apos;s by April 2027. I
                will obtain my double diploma at the end of April 2026, and I have been
                working privately with multiple clients to support them on their own
                journeys.
              </p>

              <p className="therapy__body">
                I also have my own story with mental health, and that journey brought me to
                this point in my life. It started with a roof. When I fell through one in
                March 2017, it sent me on a painful mental health journey. What left a
                lasting mark was not only the physical injury, but the emotional toll and
                mental strain it placed on my inner world.
              </p>

              <p className="therapy__body">
                From travelling the country in my van to finding myself back at home with my
                parents at 25 and having to start again, life changed quickly. After six
                months, I finally saw a psychologist and was diagnosed with PTSD. Later, I
                found my psychotherapist, who I still see today. Through that work, I came
                to understand that the accident had opened a door to wounds that had existed
                long before. What I was carrying did not begin there.
              </p>

              <p className="therapy__body">
                After healing many elements within myself, I want to help others move toward
                inner peace and a deeper understanding of self and others. In this space, I
                want to support clients through grief, loss, new beginnings, or anything
                that holds great emotional charge. Tattoos can be sacred and permanent, much
                like the scars we carry with us.
              </p>

              <p className="therapy__body">
                I look forward to working with you.
              </p>

              <h3 className="therapy__section-title">Why Tattoo Therapy</h3>

              <p className="therapy__body">
                Throughout generations and cultures around the world, tattooing has been
                embedded in ritual, new beginnings, and commemoration. Here, I aim to honour
                these traditions by bringing elements of tattoo history into a contemporary,
                therapeutic context.
              </p>

              <p className="therapy__body">
                For many people, tattooing is more than a mark on the skin. It is grounded
                in meaning, memory, and lived experience. Tattoo Therapy offers something
                beyond the tattoo itself. It provides a contained, trauma-informed space
                where you are invited to share your story, emotions, and reflections in a
                way that feels safe, respectful, and supported.
              </p>

              <p className="therapy__body">
                Over the years, I&apos;ve witnessed how deeply emotional it can be for people
                to commemorate a loved one, mark a significant time in their life, or
                reclaim a sense of self through tattooing. This work is about slowing down,
                creating space, and honouring the importance of those moments and the tattoos
                that carry them.
              </p>

              <h3 className="therapy__section-title">What This Experience Offers</h3>

              <div className="therapy__offer-list">
                <p className="therapy__body">Pre appointment check in (1 hr)</p>
                <p className="therapy__body">Tattoo &amp; therapy integration (3 hrs)</p>
                <p className="therapy__body">Counselling follow-up (1 hr)</p>
              </div>

              <div className="therapy__features" role="list">
                {therapyFeatures.map((f) => (
                  <div className="therapy__feature" key={f.title} role="listitem">
                    <span className="therapy__feature-icon" aria-hidden="true">{f.icon}</span>
                    <div>
                      <h3 className="therapy__feature-title">{f.title}</h3>
                      <p className="therapy__feature-desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="therapy__ctas">
                <button
                  type="button"
                  className="therapy__cta therapy__cta--primary"
                  onClick={openModal}
                >
                  Let&apos;s discuss your ideas - Get in Touch
                </button>

                <Link to="/book" className="therapy__cta therapy__cta--secondary">
                  Ready to Book a Session
                </Link>
              </div>
            </div>

            <div className="therapy__visual" aria-hidden="true">
              <div className="therapy__image-frame">
  <img
    src="/images/tattoo_therapy_hero.png"
    alt="Tattoo therapy session"
    className="therapy__image"
  />
</div>
              <blockquote className="therapy__quote">
                <p>
                  &quot;Ink can hold what words cant say!!&quot;
                </p>
                <cite></cite>
              </blockquote>

              <div className="therapy__pricing">
                <h3 className="therapy__section-title">Therapy Pricing</h3>

                <div className="therapy__pricing-card">
                  <h4>Tattoo Therapy Package (2 hr)</h4>
                  <p className="therapy__price">AUD $600.00</p>
                  <ul>
                    <li>Zoom or in person meeting for your comfortability (no commitment)</li>
                    <li>Complimentary pre appointment check in (30min)</li>
                    <li>(2hrs) of Tattooing</li>
                    <li>Follow up counselling session (1hr)</li>
                    <li>Smaller designs</li>
                  </ul>
                  <Link to="/book" className="therapy__cta therapy__cta--primary">
                    BOOK HERE
                  </Link>
                </div>

                <div className="therapy__pricing-card">
                  <h4>Tattoo Therapy Package (3 hr)</h4>
                  <p className="therapy__price">AUD $850.00</p>
                  <ul>
                    <li>Zoom or in person meeting for your comfortability (no commitment)</li>
                    <li>Complimentary pre appointment check in (30min)</li>
                    <li>(3hrs) of Tattooing</li>
                    <li>Follow up counselling session (1hr)</li>
                    <li>Palm size designs</li>
                  </ul>
                  <Link to="/book" className="therapy__cta therapy__cta--primary">
                    BOOK HERE
                  </Link>
                </div>

                <div className="therapy__pricing-card">
                  <h4>Tattoo Therapy Package (5 hr)</h4>
                  <p className="therapy__price">AUD $1250.00</p>
                  <ul>
                    <li>Zoom or in person meeting for your comfortability (no commitment)</li>
                    <li>Complimentary pre appointment check in (30min)</li>
                    <li>(5hrs) of Tattooing</li>
                    <li>Follow up counselling session (1hr)</li>
                    <li>Full day of tattooing (11am – 5pm)</li>
                  </ul>
                  <Link to="/book" className="therapy__cta therapy__cta--primary">
                    BOOK HERE
                  </Link>
                </div>

                <p className="therapy__pricing-note">
                  Prices reflect the two professional skills combined. Any questions please email admin@jessebtattoo.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isModalOpen && (
        <div
          className="therapy-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="therapy-contact-title"
        >
          <div className="therapy-modal__backdrop" onClick={closeModal} />
          <div className="therapy-modal__panel">
            <button
              type="button"
              className="therapy-modal__close"
              onClick={closeModal}
              aria-label="Close contact form"
            >
              ×
            </button>

            <div className="therapy-modal__header">
              <p className="therapy-modal__eyebrow">Therapy Contact Form</p>
              <h3 id="therapy-contact-title" className="therapy-modal__title">Get in Touch</h3>
              <p className="therapy-modal__intro">
                If you&apos;re interested in booking a session, complete the form with a few
                details. I will review your message and get back to you within 48 hours.
              </p>
            </div>

            <form className="therapy-form" onSubmit={handleSubmit}>
              <div className="therapy-form__grid therapy-form__grid--two">
                <div className="therapy-form__field">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  <span className="therapy-form__required">(required)</span>
                </div>

                <div className="therapy-form__field">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                  <span className="therapy-form__required">(required)</span>
                </div>
              </div>

              <div className="therapy-form__grid therapy-form__grid--two">
                <div className="therapy-form__field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <span className="therapy-form__required">(required)</span>
                </div>

                <div className="therapy-form__field">
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="therapy-form__field">
                <span className="therapy-form__label">What services are you interested in?</span>
                <div
                  className="therapy-form__choices"
                  role="radiogroup"
                  aria-label="What services are you interested in?"
                >
                  <label className={`therapy-form__choice ${formData.service === 'TATTOO THERAPY PACKAGE' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="service"
                      value="TATTOO THERAPY PACKAGE"
                      checked={formData.service === 'TATTOO THERAPY PACKAGE'}
                      onChange={handleChange}
                    />
                    <span>TATTOO THERAPY PACKAGE</span>
                  </label>

                  <label className={`therapy-form__choice ${formData.service === 'TATTOO THERAPY SESSION (3HRS)' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="service"
                      value="TATTOO THERAPY SESSION (3HRS)"
                      checked={formData.service === 'TATTOO THERAPY SESSION (3HRS)'}
                      onChange={handleChange}
                    />
                    <span>TATTOO THERAPY SESSION (3HRS)</span>
                  </label>

                  <label className={`therapy-form__choice ${formData.service === 'THERAPY SESSION (1HR)' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="service"
                      value="THERAPY SESSION (1HR)"
                      checked={formData.service === 'THERAPY SESSION (1HR)'}
                      onChange={handleChange}
                    />
                    <span>THERAPY SESSION (1HR)</span>
                  </label>
                </div>
              </div>

              <div className="therapy-form__field">
                <span className="therapy-form__label">Appointment Type</span>
                <div
                  className="therapy-form__choices"
                  role="radiogroup"
                  aria-label="Appointment Type"
                >
                  <label className={`therapy-form__choice ${formData.appointmentType === 'COMMEMORATIVE OR RITUAL BASED' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="COMMEMORATIVE OR RITUAL BASED"
                      checked={formData.appointmentType === 'COMMEMORATIVE OR RITUAL BASED'}
                      onChange={handleChange}
                      required
                    />
                    <span>COMMEMORATIVE OR RITUAL BASED</span>
                  </label>

                  <label className={`therapy-form__choice ${formData.appointmentType === 'NEW BEGINNING OR TRANSITION' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="NEW BEGINNING OR TRANSITION"
                      checked={formData.appointmentType === 'NEW BEGINNING OR TRANSITION'}
                      onChange={handleChange}
                    />
                    <span>NEW BEGINNING OR TRANSITION</span>
                  </label>

                  <label className={`therapy-form__choice ${formData.appointmentType === 'EXPLORATIVE' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="appointmentType"
                      value="EXPLORATIVE"
                      checked={formData.appointmentType === 'EXPLORATIVE'}
                      onChange={handleChange}
                    />
                    <span>EXPLORATIVE</span>
                  </label>
                </div>
                <span className="therapy-form__required">(required)</span>
              </div>

              <div className="therapy-form__field">
                <label htmlFor="preferredDate">Preferred Date</label>
                <input
                  id="preferredDate"
                  name="preferredDate"
                  type="date"
                  value={formData.preferredDate}
                  onChange={handleChange}
                />
              </div>

              <div className="therapy-form__field">
                <label htmlFor="tattooDetails">Tattoo Details</label>
                <textarea
                  id="tattooDetails"
                  name="tattooDetails"
                  rows="6"
                  value={formData.tattooDetails}
                  onChange={handleChange}
                  required
                />
                <span className="therapy-form__required">(required)</span>
              </div>

              {submitStatus.message && (
                <div
                  className={`therapy-form__status therapy-form__status--${submitStatus.type}`}
                  role="status"
                >
                  {submitStatus.message}
                </div>
              )}

              <div className="therapy-form__actions">
                <button
                  type="submit"
                  className="therapy__cta therapy__cta--primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                </button>

                <button
                  type="button"
                  className="therapy__cta therapy__cta--secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}